import React, { useEffect } from 'react'
import { SafeAreaView } from 'react-native-safe-area-context'
import { View } from 'react-native'
import { useNavigation } from '@react-navigation/native'
import { useDispatch } from 'react-redux'
import { Text } from '@src/components/base/Text'
import { BottomSheet } from '@src/components/BottomSheet'
import { Button, Icon } from '@components/base'
import { EhrAppointmentKind, SurveysConfigKind, UiStateNames } from '@src/types'
import { StyleService, useStyleSheet } from '@src/style/service'
import { useGoBack } from '@src/utils'
import { CustomEventTypes } from '@src/config/analytics'
import { Analytics } from '@src/config'

export const ChurnFeedbackModal = () => {
  const styles = useStyleSheet(themedStyles)
  const goBack = useGoBack()
  const dispatch = useDispatch()
  const navigation = useNavigation()

  const updateUiFlag = () => {
    dispatch({
      type: 'uiFlags/updateUiFlags',
      payload: {
        showChurnFeedbackModal: false,
      },
    })
    dispatch({
      type: 'app/upsertUiState',
      payload: {
        name: UiStateNames.ChurnFeedbackModal,
        value: false,
      },
      complete: () => {
        dispatch({ type: 'uiFlags/fetch' })
      },
    })
  }

  const handleOnDismiss = () => {
    Analytics.track(CustomEventTypes.ChurnFeedbackModalDismissed)
    updateUiFlag()
    goBack()
  }

  const handleOnBookCall = () => {
    updateUiFlag()

    navigation.reset({
      index: 1,
      routes: [
        { name: 'Drawer', state: { index: 0, routes: [{ name: 'Dashboard' }] } },
        {
          name: 'CallScheduling',
          params: {
            appointmentKind: EhrAppointmentKind.Followup,
            allowBackNavigation: true,
          },
        },
      ],
    })
  }

  const handleOnBegin = () => {
    updateUiFlag()

    navigation.navigate('Questionnaire', {
      questionnaire: SurveysConfigKind.ChurnFeedback,
    })
  }

  useEffect(() => {
    Analytics.track(CustomEventTypes.ChurnFeedbackModalViewed)
  }, [])

  return (
    <BottomSheet showDismissButton={false} onDismiss={handleOnDismiss}>
      <SafeAreaView edges={['bottom']} style={styles.container}>
        <Button
          accessibilityLabel="Dismiss"
          type="transparent"
          size="block"
          style={styles.dismissButton}
          textStyle={{ fontSize: 14 }}
          onPress={handleOnDismiss}
        >
          Dismiss
        </Button>

        <View style={styles.iconWrapper}>
          <View style={styles.iconInnerWrapper}>
            <Icon name="target" style={styles.icon} />
          </View>
        </View>

        <Text type="title-2" style={styles.title}>
          Still working towards your goals? Let us help.
        </Text>

        <Text type="large" style={styles.subTitle}>
          It's been a while since your last appointment. Help us keep you on track by answering a
          few questions.
        </Text>

        <View style={styles.buttons}>
          <Button accessibilityLabel="Begin" type="primary" size="block" onPress={handleOnBegin}>
            Begin
          </Button>
          <Button
            accessibilityLabel="Book a Call"
            type="transparent"
            size="block"
            onPress={handleOnBookCall}
          >
            Book a Call
          </Button>
        </View>
      </SafeAreaView>
    </BottomSheet>
  )
}

const themedStyles = StyleService.create({
  container: {
    justifyContent: 'center',
    alignItems: 'center',
  },
  iconWrapper: {
    backgroundColor: 'theme.surface.base2',
    borderRadius: 16,
    paddingVertical: 8,
    paddingHorizontal: 16,
    marginVertical: 20,
  },
  iconInnerWrapper: {
    backgroundColor: 'theme.solid.white',
    borderRadius: 100,
    padding: 12,
  },
  icon: {
    color: 'theme.info.base',
    width: 40,
    height: 40,
  },
  title: {
    textAlign: 'center',
    marginBottom: 24,
    paddingHorizontal: 16,
  },
  subTitle: {
    textAlign: 'center',
    marginBottom: 24,
  },
  buttons: {
    flexDirection: 'column',
    width: '100%',
    gap: 12,
    paddingHorizontal: 16,
  },
  dismissButton: {
    position: 'absolute',
    top: -16,
    right: 0,
  },
})
